import { Typography } from "@mui/material";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlassMagnifier } from "react-image-magnifiers";
import { Box, FlexColBox } from "../../../../../common/components";
import { MicroColorCircle } from "../../../../../common/components/Select/MicroColorCircle/MicroColorCircle";
import { palette, spacing } from "../../../../../styles/theme";
import { GLASS_MAGNIFIER_BACKGROUND_COLORS } from "../../../../../types";
import styles from "./PreviewGlassMagnifier.module.scss";
import { useStudio } from "../../../../../context/Studio";

interface Props {
  imageUrl?: string;
  imageMaxHeight?: string;
  imageQuality?: string;
  onBackgroundColorChange?: (color: GLASS_MAGNIFIER_BACKGROUND_COLORS) => void;
  backgroundColor?: GLASS_MAGNIFIER_BACKGROUND_COLORS;
  noBackgroundColor?: boolean;
  title?: string;
  allowWhite?: boolean;
  allowLightGrey?: boolean;
  hideZoom?: boolean;
}

export const PreviewGlassMagnifier = (props: Props) => {
  const { t } = useTranslation();
  const {
    imageUrl,
    imageMaxHeight,
    imageQuality,
    onBackgroundColorChange,
    backgroundColor = GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT,
    noBackgroundColor = false,
    title = "",
    allowWhite = false,
    allowLightGrey = false,
    hideZoom = false,
  } = props;
  const { isDesignLocked } = useStudio();

  const [backgroundColorState, setBackgroundColorState] = useState<
    GLASS_MAGNIFIER_BACKGROUND_COLORS | undefined
  >(undefined);
  const previewQualityColors: Record<string, string> = palette.previewQuality;

  //Initial loading
  useEffect(() => {
    if (backgroundColor) {
      setBackgroundColorState(backgroundColor);
    }
  }, [backgroundColor]);

  /**
   *
   */
  const backgroundColorChange = useCallback(
    (newColor: GLASS_MAGNIFIER_BACKGROUND_COLORS) => {
      setBackgroundColorState(newColor);
      onBackgroundColorChange?.(newColor);
    },
    [onBackgroundColorChange]
  );

  const statusColor = imageQuality
    ? (previewQualityColors[imageQuality] ?? palette.grey.primary)
    : palette.grey.primary;

  return (
    <FlexColBox gap={spacing.smallDesktop}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={"8px"}
      >
        {title && <Typography variant="textRegularBold">{title}</Typography>}

        {!noBackgroundColor && (
          <Box
            display="flex"
            alignItems="center"
            gap={spacing.smallDesktop}
            sx={{ pointerEvents: isDesignLocked ? "none" : "auto" }}
          >
            {allowWhite && (
              <Box
                onClick={() =>
                  backgroundColorChange(GLASS_MAGNIFIER_BACKGROUND_COLORS.WHITE)
                }
              >
                <MicroColorCircle
                  color={GLASS_MAGNIFIER_BACKGROUND_COLORS.WHITE}
                  tooltip={t("colors.preview.background_white")}
                  isSelected={
                    backgroundColorState ===
                    GLASS_MAGNIFIER_BACKGROUND_COLORS.WHITE
                  }
                />
              </Box>
            )}
            {allowLightGrey && (
              <Box
                onClick={() =>
                  backgroundColorChange(
                    GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY
                  )
                }
              >
                <MicroColorCircle
                  color={GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY}
                  tooltip={t("colors.preview.background_light_grey")}
                  isSelected={
                    backgroundColorState ===
                    GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY
                  }
                />
              </Box>
            )}
            <Box
              onClick={() =>
                backgroundColorChange(GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT)
              }
            >
              <MicroColorCircle
                color={GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT}
                tooltip={t("colors.preview.background_light")}
                isSelected={
                  backgroundColorState ===
                  GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT
                }
              />
            </Box>
            <Box
              onClick={() =>
                backgroundColorChange(GLASS_MAGNIFIER_BACKGROUND_COLORS.DARK)
              }
            >
              <MicroColorCircle
                color={GLASS_MAGNIFIER_BACKGROUND_COLORS.DARK}
                tooltip={t("colors.preview.background_dark")}
                isSelected={
                  backgroundColorState ===
                  GLASS_MAGNIFIER_BACKGROUND_COLORS.DARK
                }
              />
            </Box>
          </Box>
        )}

        {imageQuality && (
          <Typography variant="textSm" color={statusColor}>
            {t(`specs.decoration_quality.${imageQuality}`)}
          </Typography>
        )}
      </Box>
      <div
        className={classNames(styles.glassMagnifierContainer, "justify-center")}
      >
        {imageUrl &&
          (hideZoom ? (
            <Box
              sx={{
                background: backgroundColorState,
                borderStyle: "solid",
                borderWidth: "1px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderColor: noBackgroundColor
                  ? "transparent"
                  : backgroundColorState,
                "&>img": {
                  maxHeight: imageMaxHeight || "80vh",
                  maxWidth: "100%",
                  background: backgroundColorState,
                },
              }}
            >
              <img src={imageUrl} alt="preview" />
            </Box>
          ) : (
            <GlassMagnifier
              imageSrc={imageUrl}
              allowOverflow={true}
              magnifierSize={"120%"}
              square={false}
              imageAlt="preview"
              className={classNames(
                styles.glassMagnifier,
                noBackgroundColor
                  ? styles.noBackground
                  : backgroundColorState ===
                      GLASS_MAGNIFIER_BACKGROUND_COLORS.DARK
                    ? styles.darkBackground
                    : backgroundColorState ===
                        GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT
                      ? styles.lightBackground
                      : styles.whiteBackground
              )}
            />
          ))}
      </div>
    </FlexColBox>
  );
};
